import React, { useEffect, useState } from "react";
import { Button, Drawer, Form, Input, Space } from "antd";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import { BASE_CRM_URL } from "../../../components/config/config";

const EditTodoDrawer = ({ open, todo, onClose, onTodoUpdated }) => {
  const [form] = Form.useForm();
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (open && todo) {
      form.setFieldsValue({
        description: todo.description || "",
      });
    }
  }, [open, todo, form]);

  const handleSubmit = async (values) => {
    if (!todo) return;

    try {
      setIsUpdating(true);

      const payload = {
        todoid: todo.todoid,
        description: values.description.trim(),
        staffid: todo.staffid,
        item_order: todo.item_order || 1,
      };

      const response = await fetch(`${BASE_CRM_URL}todo/UpdateTodo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to update todo");
      }

      const data = await response.json();

      if (!data?.Status) {
        throw new Error(data?.Message || "Unable to update todo");
      }

      await Swal.fire({
        icon: "success",
        title: "Todo Updated",
        text: data?.Message || "Todo updated successfully",
        timer: 1500,
        showConfirmButton: false,
      });

      form.resetFields();

      onClose();

      if (onTodoUpdated) {
        onTodoUpdated();
      }
    } catch (error) {
      console.error("Update Todo Error:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.message || "Unable to update todo",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Drawer
      title="Edit Todo"
      placement="right"
      width={450}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <div className="d-flex justify-content-end">
          <Space>
            <Button onClick={onClose} disabled={isUpdating}>
              Cancel
            </Button>

            <Button
              type="primary"
              loading={isUpdating}
              onClick={() => form.submit()}
            >
              Update Todo
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Todo"
          name="description"
          rules={[
            {
              required: true,
              message: "Please enter todo",
            },
            {
              whitespace: true,
              message: "Todo cannot be empty",
            },
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder="Enter todo"
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

EditTodoDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  todo: PropTypes.shape({
    todoid: PropTypes.number,
    description: PropTypes.string,
    staffid: PropTypes.number,
    item_order: PropTypes.number,
    finished: PropTypes.number,
  }),
  onClose: PropTypes.func.isRequired,
  onTodoUpdated: PropTypes.func.isRequired,
};

EditTodoDrawer.defaultProps = {
  todo: null,
};

export default EditTodoDrawer;
